import { CSSProperties, useMemo } from 'react'

import { ColorVariant, RawSpacingInput } from '@makyoapp/makyo-tw-ui'

import { CoreComponentInfos } from '@/core/models/app-components/types'
import { useAppBrandKitContext } from '@/context/AppBrandKitContext'
import { useAppBuilderContext } from '@/context/AppBuilderContext'
import { useParseVariableSelect } from '@/hooks/__project__/useParseVariableSelect'
import { ComponentSettingKey } from '@/shared/builder'
import { twProse } from '@/shared/builder-utils'
import { getSafely } from '@/shared/safely'
import { CompOverlayDevmode } from '@/components/__project__/shared/CompOverlayDevmode'

import { CompRichTextProps } from './comp'

export interface CompRichTextWrapperProps {
  infos: CoreComponentInfos
  children: (props: CompRichTextProps) => React.ReactNode
}

export function CompRichTextWrapperCore({
  infos: { component, settings, containerClient, detailTableId, detailRowIndex, currentParentBg },
  children,
}: CompRichTextWrapperProps) {
  const { isDevelopmentMode, openComponentSettings } = useAppBuilderContext()
  const { brandkitTypography } = useAppBrandKitContext()

  const htmlTypography = useMemo(() => {
    if (!brandkitTypography) return {}

    return Object.fromEntries(Object.entries(brandkitTypography).filter(([key]) => key.startsWith('html')))
  }, [brandkitTypography])

  // ========================= data =========================
  const richText = useParseVariableSelect<string>(
    settings?.[ComponentSettingKey.RichTextData],
    containerClient,
    detailTableId,
    detailRowIndex
  )

  // ========================= design =========================

  // base
  const width = getSafely(settings, [ComponentSettingKey.RichTextDesignWidth], ['100%'])[0]
  const alignment = settings[ComponentSettingKey.RichTextDesignHAlign] ?? 'parent'
  const margin = settings?.[ComponentSettingKey.RichTextDesignMargin] as RawSpacingInput

  // color
  // h1
  const h1ColorType = settings[ComponentSettingKey.RichTextDesignH1Color] || 'adaptive'
  const h1ColorSolid = (settings[ComponentSettingKey.RichTextDesignH1ColorSolid] as ColorVariant) || 'primary-40'
  const h1ColorStartGradient =
    (settings[ComponentSettingKey.RichTextDesignH1ColorStartGradient] as ColorVariant) || 'primary-40'
  const h1ColorEndGradient =
    (settings[ComponentSettingKey.RichTextDesignH1ColorEndGradient] as ColorVariant) || 'primary-40'
  const h1ColorGradientDirection = settings[ComponentSettingKey.RichTextDesignH1ColorGradientDirection]
  // h2
  const h2ColorType = settings[ComponentSettingKey.RichTextDesignH2Color] || 'adaptive'
  const h2ColorSolid = (settings[ComponentSettingKey.RichTextDesignH2ColorSolid] as ColorVariant) || 'primary-40'
  const h2ColorStartGradient =
    (settings[ComponentSettingKey.RichTextDesignH2ColorStartGradient] as ColorVariant) || 'primary-40'
  const h2ColorEndGradient =
    (settings[ComponentSettingKey.RichTextDesignH2ColorEndGradient] as ColorVariant) || 'primary-40'
  const h2ColorGradientDirection = settings[ComponentSettingKey.RichTextDesignH2ColorGradientDirection]
  // h3
  const h3ColorType = settings[ComponentSettingKey.RichTextDesignH3Color] || 'adaptive'
  const h3ColorSolid = (settings[ComponentSettingKey.RichTextDesignH3ColorSolid] as ColorVariant) || 'primary-40'
  const h3ColorStartGradient =
    (settings[ComponentSettingKey.RichTextDesignH3ColorStartGradient] as ColorVariant) || 'primary-40'
  const h3ColorEndGradient =
    (settings[ComponentSettingKey.RichTextDesignH3ColorEndGradient] as ColorVariant) || 'primary-40'
  const h3ColorGradientDirection = settings[ComponentSettingKey.RichTextDesignH3ColorGradientDirection]
  // h4
  const h4ColorType = settings[ComponentSettingKey.RichTextDesignH4Color] || 'adaptive'
  const h4ColorSolid = (settings[ComponentSettingKey.RichTextDesignH4ColorSolid] as ColorVariant) || 'primary-40'
  const h4ColorStartGradient =
    (settings[ComponentSettingKey.RichTextDesignH4ColorStartGradient] as ColorVariant) || 'primary-40'
  const h4ColorEndGradient =
    (settings[ComponentSettingKey.RichTextDesignH4ColorEndGradient] as ColorVariant) || 'primary-40'
  const h4ColorGradientDirection = settings[ComponentSettingKey.RichTextDesignH4ColorGradientDirection]
  // h5
  const h5ColorType = settings[ComponentSettingKey.RichTextDesignH5Color] || 'adaptive'
  const h5ColorSolid = (settings[ComponentSettingKey.RichTextDesignH5ColorSolid] as ColorVariant) || 'primary-40'
  const h5ColorStartGradient =
    (settings[ComponentSettingKey.RichTextDesignH5ColorStartGradient] as ColorVariant) || 'primary-40'
  const h5ColorEndGradient =
    (settings[ComponentSettingKey.RichTextDesignH5ColorEndGradient] as ColorVariant) || 'primary-40'
  const h5ColorGradientDirection = settings[ComponentSettingKey.RichTextDesignH5ColorGradientDirection]
  // h6
  const h6ColorType = settings[ComponentSettingKey.RichTextDesignH6Color] || 'adaptive'
  const h6ColorSolid = (settings[ComponentSettingKey.RichTextDesignH6ColorSolid] as ColorVariant) || 'primary-40'
  const h6ColorStartGradient =
    (settings[ComponentSettingKey.RichTextDesignH6ColorStartGradient] as ColorVariant) || 'primary-40'
  const h6ColorEndGradient =
    (settings[ComponentSettingKey.RichTextDesignH6ColorEndGradient] as ColorVariant) || 'primary-40'
  const h6ColorGradientDirection = settings[ComponentSettingKey.RichTextDesignH6ColorGradientDirection]
  // p
  const pColorType = settings[ComponentSettingKey.RichTextDesignPColor] || 'adaptive'
  const pColorSolid = (settings[ComponentSettingKey.RichTextDesignPColorSolid] as ColorVariant) || 'primary-40'
  const pColorStartGradient =
    (settings[ComponentSettingKey.RichTextDesignPColorStartGradient] as ColorVariant) || 'primary-40'
  const pColorEndGradient =
    (settings[ComponentSettingKey.RichTextDesignPColorEndGradient] as ColorVariant) || 'primary-40'
  const pColorGradientDirection = settings[ComponentSettingKey.RichTextDesignPColorGradientDirection]
  // ol
  const olColorType = settings[ComponentSettingKey.RichTextDesignOlColor] || 'adaptive'
  const olColorSolid = (settings[ComponentSettingKey.RichTextDesignOlColorSolid] as ColorVariant) || 'primary-40'
  const olColorStartGradient =
    (settings[ComponentSettingKey.RichTextDesignOlColorStartGradient] as ColorVariant) || 'primary-40'
  const olColorEndGradient =
    (settings[ComponentSettingKey.RichTextDesignOlColorEndGradient] as ColorVariant) || 'primary-40'
  const olColorGradientDirection = settings[ComponentSettingKey.RichTextDesignOlColorGradientDirection]
  // ul
  const ulColorType = settings[ComponentSettingKey.RichTextDesignUlColor] || 'adaptive'
  const ulColorSolid = (settings[ComponentSettingKey.RichTextDesignUlColorSolid] as ColorVariant) || 'primary-40'
  const ulColorStartGradient =
    (settings[ComponentSettingKey.RichTextDesignUlColorStartGradient] as ColorVariant) || 'primary-40'
  const ulColorEndGradient =
    (settings[ComponentSettingKey.RichTextDesignUlColorEndGradient] as ColorVariant) || 'primary-40'
  const ulColorGradientDirection = settings[ComponentSettingKey.RichTextDesignUlColorGradientDirection]

  // check if rich text empty
  if (!richText || !richText.trim().length) return null

  const handleClick = () => {
    openComponentSettings(component)
    if (isDevelopmentMode) return
  }

  const props: CompRichTextProps = {
    // base
    id: component._id,
    width,
    alignment,
    margin,
    backgroundColor: currentParentBg,
    textAlignment: alignment,

    // rich text
    text: richText,
    style: {
      ...twProse(),
      ...{
        '--color-fg-default': `var(--color-on-${currentParentBg})`,
        '--color-neutral-muted': `var(--color-on-${currentParentBg})`,
      },
    } as CSSProperties,

    // color
    h1ColorType,
    h1ColorSolid,
    h1ColorStartGradient,
    h1ColorEndGradient,
    h1ColorGradientDirection,
    h2ColorType,
    h2ColorSolid,
    h2ColorStartGradient,
    h2ColorEndGradient,
    h2ColorGradientDirection,
    h3ColorType,
    h3ColorSolid,
    h3ColorStartGradient,
    h3ColorEndGradient,
    h3ColorGradientDirection,
    h4ColorType,
    h4ColorSolid,
    h4ColorStartGradient,
    h4ColorEndGradient,
    h4ColorGradientDirection,
    h5ColorType,
    h5ColorSolid,
    h5ColorStartGradient,
    h5ColorEndGradient,
    h5ColorGradientDirection,
    h6ColorType,
    h6ColorSolid,
    h6ColorStartGradient,
    h6ColorEndGradient,
    h6ColorGradientDirection,
    pColorType,
    pColorSolid,
    pColorStartGradient,
    pColorEndGradient,
    pColorGradientDirection,
    olColorType,
    olColorSolid,
    olColorStartGradient,
    olColorEndGradient,
    olColorGradientDirection,
    ulColorType,
    ulColorSolid,
    ulColorStartGradient,
    ulColorEndGradient,
    ulColorGradientDirection,

    htmlTypography,
  }

  if (isDevelopmentMode) {
    return (
      <div className='relative border border-dashed flex flex-col' style={{ borderColor: '#333' }}>
        {children(props)}
        <CompOverlayDevmode handleClick={handleClick} />
      </div>
    )
  }

  return children(props)
}
